import { some } from "lodash";
import { CircleHelp, Loader } from "lucide-react";
import { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useUpdateSKUMutation } from "@/data/mutations";
import { SKU } from "@/data/types";
import { SKUFormValues } from "@/lib/forms";
import { canEditSKU } from "@/lib/permissions";
import { FileType, MaxFileSizeBytes } from "@/lib/uploads";
import useStore from "@/state";

import { FileUploadInput } from "./file-upload-input";
import { ProductPreview } from "./product-preview";

type Props = {
  sku: SKU;
};

const resolveDirtyFields = (
  fields: Record<string, boolean | boolean[] | undefined>,
) => {
  return some([
    fields.name,
    fields.image_url,
    fields.animation_url,
    fields.description,
  ]);
};

const PRODUCT_IMAGE_MIN_WIDTH = 400;
const PRODUCT_IMAGE_MAX_HEIGHT = 600;

export const ProductTab: FC<Props> = ({ sku }) => {
  const form = useFormContext<SKUFormValues>();

  const { session } = useStore();

  const updateSKUMutation = useUpdateSKUMutation(sku.id);

  const handleSubmit = async (values: SKUFormValues) => {
    await updateSKUMutation.mutateAsync({
      name: values.name,
      image_url: values.image_url,
      animation_url: values.animation_url,
      description: values.description,
    });

    form.reset({}, { keepValues: true });
  };

  const hasPermission = useMemo(() => {
    return canEditSKU(session?.permissions);
  }, [session?.permissions]);

  const isDirty = resolveDirtyFields(form.formState.dirtyFields);

  const isSubmitEnabled = useMemo(() => {
    const isValid = form.formState.isValid;
    const isPending = updateSKUMutation.isPending;

    return hasPermission && isDirty && isValid && !isPending;
  }, [
    hasPermission,
    isDirty,
    form.formState.isValid,
    updateSKUMutation.isPending,
  ]);

  return (
    <div className="flex flex-col lg:flex-row gap-6 mt-6">
      <div className="flex-none lg:flex-1 lg:min-w-[320px]">
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <FormField
            control={form.control}
            name="name"
            disabled={!hasPermission}
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel>Product Name*</FormLabel>
                <FormControl>
                  <Input placeholder="" {...field} />
                </FormControl>
                <FormDescription>
                  This is the display name of the SKU and its associated Digital
                  Product. Max 75 characters.
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="image_url"
            disabled={!hasPermission}
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel>Image</FormLabel>
                <FormControl>
                  <FileUploadInput
                    {...field}
                    fileType={FileType.IMAGE}
                    minWidth={PRODUCT_IMAGE_MIN_WIDTH}
                    maxHeight={PRODUCT_IMAGE_MAX_HEIGHT}
                  />
                </FormControl>
                <FormDescription>
                  Product image in PNG, JPG, WebP, GIF, or SVG format. Maximum
                  file size 10MB. Minimum width {PRODUCT_IMAGE_MIN_WIDTH}px and
                  maximum height {PRODUCT_IMAGE_MAX_HEIGHT}px. Image will keep
                  its original aspect ratio and will not be cropped.
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="animation_url"
            disabled={!hasPermission}
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel>Video</FormLabel>
                <FormControl>
                  <FileUploadInput
                    {...field}
                    fileType={FileType.VIDEO}
                    maxFileSize={MaxFileSizeBytes.CloudflareVideo}
                  />
                </FormControl>
                <FormDescription>
                  Animation for product in MOV, WebM - 1:1 ratio recommended.
                  Max 200MB.
                </FormDescription>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            disabled={!hasPermission}
            render={({ field }) => (
              <FormItem className="mt-6">
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Experience our innovative spring collection, featuring sustainably sourced materials, and contemporary designs. Each piece has a refined blend of cotton and polyester to create a balance of breathability and durability."
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  The description can be viewed by end-consumers. Markdown
                  formatting is supported.
                </FormDescription>
              </FormItem>
            )}
          />

          <div className="mt-6 flex gap-x-2">
            <CircleHelp className="text-muted-foreground" />
            <p className="text-sm text-muted-foreground font-medium">
              View our{" "}
              <a
                className="underline text-black"
                href="https://docs.legitimate.tech/public-docs/project-docs/required-activation-assets/image-specs"
                target="_blank"
                rel="noopener noreferrer"
              >
                documentation
              </a>{" "}
              on required assets for Digital Products for in-depth requirements
              for image and video assets.
            </p>
          </div>

          <Button className="mt-6" type="submit" disabled={!isSubmitEnabled}>
            {updateSKUMutation.isPending ? (
              <Loader className="animate-spin" />
            ) : null}
            Save
          </Button>
        </form>
      </div>
      <ProductPreview sku={sku} />
    </div>
  );
};
